import { useState, useRef, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";

export interface DivisionMarker {
  divisionDescriptor: string;
  x: number;
  y: number;
  lateArrival?: boolean;
}

interface DivisionAnalysisMapProps {
  markers?: DivisionMarker[];
  selectedDivisionDescriptor?: string;
  onDivisionClick?: (descriptor: string) => void;
  onCoordinateClick?: (x: number, y: number) => void;
  showCoordinates?: boolean;
  editMode?: boolean;
  coordinatePlacementMode?: boolean; // When true, clicks place coordinates; when false, clicks pan
}

const SVG_VIEWBOX = { x: 0, y: 0, width: 12300, height: 11200 };

export function DivisionAnalysisMap({
  markers = [],
  selectedDivisionDescriptor,
  onDivisionClick,
  onCoordinateClick,
  showCoordinates = false,
  editMode = false,
  coordinatePlacementMode = false,
}: DivisionAnalysisMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgLoaded, setSvgLoaded] = useState(false);
  // Start with a reasonable initial transform - will be recalculated on mount
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 0.1 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Convert screen coordinates to SVG coordinates
  const screenToSvg = useCallback((screenX: number, screenY: number): { x: number; y: number } => {
    if (!svgRef.current || !containerRef.current) {
      return { x: 0, y: 0 };
    }

    const rect = containerRef.current.getBoundingClientRect();
    const svgRect = svgRef.current.getBoundingClientRect();
    
    // Account for transform
    const x = (screenX - rect.left - transform.x) / transform.scale;
    const y = (screenY - rect.top - transform.y) / transform.scale;

    return { x, y };
  }, [transform]);

  // Handle mouse move for coordinate display
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    const svgCoords = screenToSvg(e.clientX, e.clientY);
    setMousePosition(svgCoords);

    if (isDragging) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      setTransform(prev => ({
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  }, [isDragging, dragStart, screenToSvg]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // Only left mouse button
    
    // In edit mode, only place coordinates if coordinatePlacementMode is true
    // Otherwise, allow panning. Also allow panning with middle mouse button or space key
    if (editMode && coordinatePlacementMode && onCoordinateClick && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      const svgCoords = screenToSvg(e.clientX, e.clientY);
      onCoordinateClick(Math.round(svgCoords.x), Math.round(svgCoords.y));
    } else {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  }, [editMode, coordinatePlacementMode, onCoordinateClick, screenToSvg]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.1, Math.min(2, transform.scale * zoomFactor));

    // Zoom towards mouse position
    const scaleChange = newScale / transform.scale;
    const newX = mouseX - (mouseX - transform.x) * scaleChange;
    const newY = mouseY - (mouseY - transform.y) * scaleChange;

    setTransform({
      x: newX,
      y: newY,
      scale: newScale,
    });
  }, [transform]);

  // Reset zoom/pan
  const resetView = useCallback(() => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const containerWidth = rect.width || 800; // Fallback width
    const containerHeight = rect.height || 600; // Fallback height
    
    // Use a reasonable initial scale - show about 1/4 to 1/5 of the map
    // SVG viewBox is 12300x11200, container might be ~400-800px
    // Calculate scale to show a good portion of the map
    const scaleX = (containerWidth / SVG_VIEWBOX.width) * 4; // Show 1/4 horizontally
    const scaleY = (containerHeight / SVG_VIEWBOX.height) * 4; // Show 1/4 vertically
    const scale = Math.max(scaleX, scaleY);
    
    // Center on coordinates 4750, 6200
    const centerX = 4750;
    const centerY = 6200;
    
    // Position so the center point is at the center of the container
    const x = containerWidth / 2 - centerX * scale;
    const y = containerHeight / 2 - centerY * scale;
    
    setTransform({ x, y, scale });
    setInitialized(true);
  }, []);

  useEffect(() => {
    // Reset view when container size changes or on initial mount
    const reset = () => {
      if (containerRef.current) {
        resetView();
      }
    };
    
    // Initial reset with a small delay to ensure container is rendered
    const timer = setTimeout(reset, 100);
    
    // Also reset on window resize
    window.addEventListener('resize', reset);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', reset);
    };
  }, [resetView]);

  return (
    <Card className="relative w-full h-full overflow-hidden bg-gray-100 dark:bg-gray-900">
      <div
        ref={containerRef}
        className="w-full h-full relative"
        style={{ 
          cursor: (editMode && coordinatePlacementMode) ? "crosshair" : isDragging ? "grabbing" : "grab",
          minHeight: "400px",
          background: "rgba(0, 0, 0, 0.1)"
        }}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <svg
          ref={svgRef}
          viewBox={`${SVG_VIEWBOX.x} ${SVG_VIEWBOX.y} ${SVG_VIEWBOX.width} ${SVG_VIEWBOX.height}`}
          className="absolute"
          width={SVG_VIEWBOX.width}
          height={SVG_VIEWBOX.height}
          preserveAspectRatio="none"
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            transformOrigin: "0 0",
          }}
        >
          {/* Render SVG background as image */}
          <image
            href="/images/branding/division-analysis-background-expanded.svg"
            x={SVG_VIEWBOX.x}
            y={SVG_VIEWBOX.y}
            width={SVG_VIEWBOX.width}
            height={SVG_VIEWBOX.height}
            preserveAspectRatio="none"
            onLoad={() => {
              setSvgLoaded(true);
              console.log("SVG image loaded successfully");
            }}
            onError={(e) => {
              console.error("Failed to load SVG image:", e);
            }}
          />

          {/* Render division markers */}
          {markers.map((marker) => {
            const isSelected = marker.divisionDescriptor === selectedDivisionDescriptor;
            const markerSize = isSelected ? 96 : 64;
            
            return (
              <g key={marker.divisionDescriptor}>
                {/* Late arrival border */}
                {marker.lateArrival && (
                  <rect
                    x={marker.x - markerSize / 2 - 4}
                    y={marker.y - markerSize / 2 - 4}
                    width={markerSize + 8}
                    height={markerSize + 8}
                    fill="none"
                    stroke="#6493c6"
                    strokeWidth={2 / transform.scale}
                    strokeDasharray={`${5 / transform.scale},${5 / transform.scale}`}
                  />
                )}
                
                {/* Division image */}
                <image
                  href={`/images/divisions/${marker.divisionDescriptor}.png`}
                  x={marker.x - markerSize / 2}
                  y={marker.y - markerSize / 2}
                  width={markerSize}
                  height={markerSize}
                  opacity={isSelected ? 1 : 0.5}
                  style={{ cursor: "pointer" }}
                  onClick={() => onDivisionClick?.(marker.divisionDescriptor)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.setAttribute("width", String(markerSize * 1.5));
                    e.currentTarget.setAttribute("height", String(markerSize * 1.5));
                    e.currentTarget.setAttribute("x", String(marker.x - (markerSize * 1.5) / 2));
                    e.currentTarget.setAttribute("y", String(marker.y - (markerSize * 1.5) / 2));
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = isSelected ? "1" : "0.5";
                    e.currentTarget.setAttribute("width", String(markerSize));
                    e.currentTarget.setAttribute("height", String(markerSize));
                    e.currentTarget.setAttribute("x", String(marker.x - markerSize / 2));
                    e.currentTarget.setAttribute("y", String(marker.y - markerSize / 2));
                  }}
                />
              </g>
            );
          })}
        </svg>

        {/* Coordinate display */}
        {showCoordinates && mousePosition && (
          <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-2 rounded text-sm font-mono">
            X: {Math.round(mousePosition.x)}, Y: {Math.round(mousePosition.y)}
          </div>
        )}

        {/* Zoom controls */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <button
            onClick={() => setTransform(prev => ({ ...prev, scale: Math.min(2, prev.scale * 1.2) }))}
            className="bg-black/70 text-white px-3 py-1 rounded text-sm hover:bg-black/90"
          >
            +
          </button>
          <button
            onClick={() => setTransform(prev => ({ ...prev, scale: Math.max(0.1, prev.scale * 0.8) }))}
            className="bg-black/70 text-white px-3 py-1 rounded text-sm hover:bg-black/90"
          >
            −
          </button>
          <button
            onClick={resetView}
            className="bg-black/70 text-white px-3 py-1 rounded text-sm hover:bg-black/90"
            title="Reset view"
          >
            ⟲
          </button>
        </div>
      </div>
    </Card>
  );
}

